import React,{useEffect,useRef,useState} from 'react';
import {FiCheck,FiChevronDown,FiLoader,FiX} from 'react-icons/fi';
import {resolveApiBase} from '../lib/transcriptionApi.js';
import {storyFlowSignature,flowMoveCount,applySuggestedFlow,suggestedFlowBlocks} from '../lib/storyFlowSuggestion.js';

/** One click starts AI. The Assembly itself previews the proposed order; this is only its compact confirmation strip. */
export default function StoryFlowPanel({paper,getAccessToken,onPreview,onApply,onClose}){
 const [proposal,setProposal]=useState(null);
 const [applyHeadings,setApplyHeadings]=useState(false);
 const [busy,setBusy]=useState(true),[error,setError]=useState('');
 const request=useRef(null);
 const initial=useRef(storyFlowSignature(paper));
 const latest=useRef(storyFlowSignature(paper));
 latest.current=storyFlowSignature(paper);

 useEffect(()=>{
  const controller=new AbortController();
  request.current=controller;
  const original=paper;
  const run=async()=>{
   try{
    const token=await getAccessToken();
    if(!token)throw new Error('Sign in to use AI Suggest Flow.');
    const response=await fetch(resolveApiBase()+'/api/story-flow',{
     method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
     signal:AbortSignal.any([controller.signal,AbortSignal.timeout(100000)]),
     body:JSON.stringify({
      brief:'',passages:original.blocks.map(b=>({
       id:b.id,source:original.sources?.[b.sourceId]?.title||'Interview',
       text:b.text,seconds:b.end-b.start,section:b.editorialSection||'',priority:b.priority||''
      })),
      sections:(original.sectionHeadings||[]).map(h=>({title:h.title,beforeIndex:h.beforeIndex}))
     })
    });
    const data=await response.json().catch(()=>({}));
    if(!response.ok)throw new Error(data.error||'Could not suggest an order.');
    if(controller.signal.aborted)return;
    if(initial.current!==latest.current)throw new Error('The Assembly changed. Try AI Suggest Flow again.');
    suggestedFlowBlocks(original,data.orderedIds);
    if(!Array.isArray(data.decisions)||!Array.isArray(data.headings)||
      data.headings.some(h=>!original.blocks.some(b=>b.id===h.beforeId)))
      throw new Error('AI returned incomplete ordering explanations.');
    setProposal(data);
    onPreview(data.orderedIds.some((id,i)=>id!==original.blocks[i].id)?data.orderedIds:null);
   }catch(e){
    if(!controller.signal.aborted)setError(e.name==='TimeoutError'?'AI took too long. Try again.':e.message);
   }finally{if(request.current===controller){request.current=null;setBusy(false);}}
  };
  run();
  return ()=>{controller.abort();request.current=null;};
 },[]);

 useEffect(()=>{
  if(initial.current!==latest.current){
   request.current?.abort();
   onClose();
  }
 },[paper]);

 const discard=()=>{request.current?.abort();onPreview(null);onClose();};
 const changed=proposal&&flowMoveCount(paper,proposal.orderedIds)>0;
 const canApply=proposal&&(changed||(applyHeadings&&proposal.headings?.length))&&
  latest.current===initial.current&&(!applyHeadings||proposal.headings.every(h=>h.title?.trim()));
 const apply=()=>{
  if(!canApply)return;
  try{
   const next=applySuggestedFlow(paper,proposal,{applyHeadings});
   onPreview(null);
   onApply(next);
   onClose();
  }catch(e){setError(e.message||'Could not apply the suggested order.');}
 };
 return <div className={'story-flow-inline'+(proposal?' is-preview':'')} role="region" aria-label="AI Suggest Flow">
  <div className="story-flow-inline-main">
   <span className="story-flow-inline-symbol" aria-hidden="true">✦</span>
   <div className="story-flow-inline-copy" aria-live="polite">
    <strong>{busy?'AI is reviewing your Assembly…':error?'Could not suggest a flow':changed?'Suggested order preview':proposal?'Your current order looks good':'AI Suggest Flow'}</strong>
    <span>{busy?'Your current edit stays unchanged.':error?error:changed?
      flowMoveCount(paper,proposal.orderedIds)+' passages would move · Nothing changed yet':
      proposal?'No rearrangement needed.':'Only existing passages are considered.'}</span>
   </div>
   <div className="story-flow-inline-actions">
    {busy?<FiLoader className="story-flow-inline-spinner" aria-label="Loading"/>:null}
    {canApply&&<button type="button" className="story-flow-inline-apply" onClick={apply}><FiCheck aria-hidden="true"/> Apply order</button>}
    <button type="button" className="story-flow-inline-discard" onClick={discard}>
     <FiX aria-hidden="true"/><span>{busy?'Cancel':'Dismiss'}</span>
    </button>
   </div>
  </div>
  {proposal&&(proposal.reason||proposal.headings?.length>0)&&<details className="story-flow-inline-details">
   <summary>Why this order{proposal.headings?.length?' · Chapters available':''}<FiChevronDown aria-hidden="true"/></summary>
   {proposal.reason&&<p>{proposal.reason}</p>}
   {proposal.headings?.length>0&&<label>
    <input type="checkbox" checked={applyHeadings} onChange={e=>setApplyHeadings(e.target.checked)}/>
    Replace existing headings with AI-suggested chapters
   </label>}
   {applyHeadings&&proposal.headings?.length>0&&<div className="story-flow-inline-chapters">
    {proposal.headings.map(h=><label key={h.beforeId}>
     <span>Before passage {proposal.orderedIds.indexOf(h.beforeId)+1}</span>
     <input value={h.title} maxLength={80} onChange={event=>setProposal(old=>({...old,headings:old.headings.map(item=>item.beforeId===h.beforeId?{...item,title:event.target.value}:item)}))}/>
    </label>)}
   </div>}
  </details>}
  {changed&&<span className="story-flow-inline-footnote">Previewing in Assembly below · Editing is paused until you apply or dismiss</span>}
 </div>;
}
